import React, { useState, useRef } from 'react';
import { useLang } from '../../i18n/LangContext';
import { EMERGENCY_CATEGORIES } from '../../data/siteData';
import { Siren, Upload, X, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { appendAttribution } from '../../utils/leadTracking';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmergencyForm() {
  const { lang, t } = useLang();
  const e = t.emergency;
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', city: '', zip: '',
    categoryIndex: -1, description: '', issueStarted: '', activeDamage: '',
    contactMethod: e.contacts[0],
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFiles = (ev) => {
    const newFiles = Array.from(ev.target.files || []);
    const maxSize = 25 * 1024 * 1024;
    setFiles(prev => [...prev, ...newFiles.filter(f => f.size <= maxSize)]);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.name || !form.phone) { toast.error(e.required); return; }
    if (form.categoryIndex < 0) { toast.error(e.selectCategory); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('phone', form.phone);
      fd.append('email', form.email);
      fd.append('address', form.address);
      fd.append('city', form.city);
      fd.append('zip', form.zip);
      fd.append('category', EMERGENCY_CATEGORIES[form.categoryIndex]);
      fd.append('description', form.description);
      fd.append('issueStarted', form.issueStarted);
      fd.append('activeDamage', form.activeDamage);
      fd.append('contactMethod', form.contactMethod);
      fd.append('language', lang);
      appendAttribution(fd, 'Emergency Services');
      files.forEach(f => fd.append('files', f));
      const res = await fetch(`${API}/api/emergency`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Failed');
      toast.success(e.success);
      setForm({ name: '', phone: '', email: '', address: '', city: '', zip: '', categoryIndex: -1, description: '', issueStarted: '', activeDamage: '', contactMethod: e.contacts[0] });
      setFiles([]);
    } catch { toast.error(e.error); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#E11D2A]/60 focus:outline-none";
  const labelCls = "text-slate-400 text-xs mb-1.5 block";

  return (
    <form onSubmit={handleSubmit} data-testid="emergency-form" className="bg-[#12151A] rounded-lg border border-[#E11D2A]/25 p-6 sm:p-10">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div><label className={labelCls}>{e.name} *</label><input data-testid="emergency-input-name" required value={form.name} onChange={ev => set('name', ev.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>{e.phone} *</label><input data-testid="emergency-input-phone" required value={form.phone} onChange={ev => set('phone', ev.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>{e.email}</label><input data-testid="emergency-input-email" type="email" value={form.email} onChange={ev => set('email', ev.target.value)} className={inputCls} /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div><label className={labelCls}>{e.address}</label><input value={form.address} onChange={ev => set('address', ev.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>{e.city}</label><input value={form.city} onChange={ev => set('city', ev.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>{e.zip}</label><input value={form.zip} onChange={ev => set('zip', ev.target.value)} className={inputCls} /></div>
      </div>

      <div className="mb-6">
        <label className={labelCls}>{e.categoriesLabel} *</label>
        <div className="flex flex-wrap gap-2" data-testid="emergency-categories">
          {e.categories.map((label, i) => (
            <button key={i} type="button" data-testid={`emergency-cat-${i}`} onClick={() => set('categoryIndex', i)}
              className={`px-3.5 py-2 rounded border text-xs transition-all ${form.categoryIndex === i ? 'border-[#E11D2A] bg-[#E11D2A]/15 text-[#FF6B73]' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>{label}</button>
          ))}
        </div>
      </div>

      <div className="mb-6"><label className={labelCls}>{e.describe}</label>
        <textarea data-testid="emergency-textarea" rows={4} value={form.description} onChange={ev => set('description', ev.target.value)} className={`${inputCls} resize-none`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div><label className={labelCls}>{e.started}</label><input placeholder={e.startedPlaceholder} value={form.issueStarted} onChange={ev => set('issueStarted', ev.target.value)} className={inputCls} /></div>
        <div>
          <label className={labelCls}>{e.activeDamage}</label>
          <div className="flex gap-2">
            {[e.yes, e.no].map((opt, i) => (
              <button key={opt} type="button" data-testid={`emergency-damage-${i === 0 ? 'yes' : 'no'}`} onClick={() => set('activeDamage', i === 0 ? 'Yes' : 'No')}
                className={`flex-1 py-3 rounded border text-sm transition-all ${(form.activeDamage === 'Yes' && i === 0) || (form.activeDamage === 'No' && i === 1) ? 'border-[#E11D2A] bg-[#E11D2A]/15 text-[#FF6B73]' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>{opt}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className={labelCls}>{e.contactPref}</label>
        <select value={form.contactMethod} onChange={ev => set('contactMethod', ev.target.value)} className={inputCls} data-testid="emergency-contact-method">
          {e.contacts.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="mb-8">
        <label className={labelCls}>{e.uploadLabel}</label>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/jpg,image/heic,application/pdf" onChange={handleFiles} className="hidden" />
        <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 border border-dashed border-white/20 hover:border-[#E11D2A]/40 rounded px-4 py-3 text-slate-400 text-sm transition-colors">
          <Upload className="w-4 h-4" />{e.uploadHint}
        </button>
        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-[#0B0D0F] rounded px-3 py-2">
                <div className="flex items-center gap-2 text-sm text-slate-300 truncate"><FileText className="w-4 h-4 text-[#E11D2A] flex-shrink-0" /><span className="truncate">{f.name}</span><span className="text-slate-500 text-xs flex-shrink-0">({(f.size / 1024 / 1024).toFixed(1)}MB)</span></div>
                <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2.5 bg-[#E11D2A]/10 border border-[#E11D2A]/25 rounded px-4 py-3 mb-4">
        <AlertTriangle className="w-4 h-4 text-[#FF6B73] flex-shrink-0 mt-0.5" />
        <p className="text-[#FFB3B8] text-xs leading-relaxed">{e.warning911}</p>
      </div>
      <p className="text-slate-400 text-xs mb-6">{e.notice}</p>

      <button type="submit" disabled={loading} data-testid="emergency-submit-button"
        className="w-full group flex items-center justify-center gap-3 bg-[#E11D2A] hover:bg-[#C41623] disabled:opacity-50 text-white font-bold py-4 rounded text-base transition-all">
        <Siren className="w-5 h-5" />{loading ? e.submitting : e.submit}
      </button>
    </form>
  );
}
